import React, { useState, useEffect } from 'react';
import configService from '../services/configService';

function ReferralManager() {
  const [referrals, setReferrals] = useState([]);
  const [referralLink, setReferralLink] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [sendingEmails, setSendingEmails] = useState(new Set());
  const [emailSuccess, setEmailSuccess] = useState('');
  const [analytics, setAnalytics] = useState({
    totalReferrals: 0,
    completedReferrals: 0,
    conversionRate: 0,
    totalCreditsEarned: 0
  });
  const [hideCanceled, setHideCanceled] = useState(true);

  useEffect(() => {
    loadReferrals();
    loadReferralLink();
  }, []);

  const loadReferrals = async () => {
    try {
      const response = await configService.getUserReferrals();
      const referralsList = response.referrals || [];
      setReferrals(referralsList);
      
      // Calculate analytics
      const completed = referralsList.filter(r => r.status === 'complete');
      const totalCredits = completed.reduce((sum, r) => sum + (r.credit_amount || 0), 0);
      
      setAnalytics({
        totalReferrals: referralsList.length,
        completedReferrals: completed.length,
        conversionRate: referralsList.length > 0 ? (completed.length / referralsList.length * 100) : 0,
        totalCreditsEarned: totalCredits
      });
    } catch (error) {
      console.error('Failed to load referrals:', error);
      setError('Failed to load referrals');
    } finally {
      setLoading(false);
    }
  };

  const loadReferralLink = async () => {
    try {
      const response = await configService.getReferralLink();
      setReferralLink(response.referral_link || '');
    } catch (error) {
      console.error('Failed to load referral link:', error);
    }
  };

  const createReferral = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;

    setCreating(true);
    setError('');
    setEmailSuccess('');

    try {
      await configService.createReferral(newEmail.trim());
      setNewEmail('');
      await loadReferrals();
    } catch (error) {
      console.error('Failed to create referral:', error);
      setError(error.message || 'Failed to create referral');
    } finally {
      setCreating(false);
    }
  };

  const sendInvitationEmail = async (email) => {
    setSendingEmails(prev => new Set([...prev, email]));
    setError('');
    setEmailSuccess('');

    try {
      await configService.sendReferralInvitation(email, 'Your friend');
      setEmailSuccess(`Invitation email sent to ${email}!`);
      setTimeout(() => setEmailSuccess(''), 5000);
    } catch (error) {
      console.error('Failed to send invitation:', error);
      setError(error.message || 'Failed to send invitation email');
    } finally {
      setSendingEmails(prev => {
        const newSet = new Set(prev);
        newSet.delete(email);
        return newSet;
      });
    }
  };

  const cancelReferral = async (referralId) => {
    try {
      await configService.cancelReferral(referralId);
      await loadReferrals();
    } catch (error) {
      console.error('Failed to cancel referral:', error);
      setError(error.message || 'Failed to cancel referral');
    }
  };

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);
    const button = document.getElementById('copy-link-btn');
    const originalText = button.textContent;
    button.textContent = 'Copied!';
    setTimeout(() => {
      button.textContent = originalText;
    }, 2000);
  };

  const getStatusBadge = (status) => {
    const badges = {
      no_response: { color: 'bg-gray-500', text: 'No Response', icon: '⏳' },
      trial: { color: 'bg-blue-500', text: 'Trial Active', icon: '🔄' },
      complete: { color: 'bg-green-500', text: 'Complete', icon: '✅' },
      cancelled: { color: 'bg-red-500', text: 'Cancelled', icon: '❌' }
    };
    const badge = badges[status] || badges.no_response;
    return (
      <span className={`px-3 py-1 rounded-full text-xs text-white ${badge.color} flex items-center gap-1`}>
        <span>{badge.icon}</span>
        {badge.text}
      </span>
    );
  };

  const getProgressBar = (status) => {
    const progress = {
      no_response: 25,
      trial: 75,
      complete: 100,
      cancelled: 0
    };
    const percent = progress[status] || 0;
    const color = status === 'complete' ? 'bg-green-500' : status === 'trial' ? 'bg-blue-500' : 'bg-gray-300';
    
    return (
      <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
        <div className={`${color} h-2 rounded-full transition-all duration-300`} style={{width: `${percent}%`}}></div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white p-8 rounded-2xl shadow-sm border">
        <div className="text-center">
          <div className="w-6 h-6 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading referrals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-8 rounded-2xl shadow-sm border">
      <h3 className="text-xl font-bold text-gray-900 mb-6">Referral Program</h3>
      
      {/* Analytics Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-blue-50 p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-blue-600">{analytics.totalReferrals}</div>
          <div className="text-sm text-blue-700">Total Referrals</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-green-600">{analytics.completedReferrals}</div>
          <div className="text-sm text-green-700">Completed</div>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-purple-600">{analytics.conversionRate.toFixed(1)}%</div>
          <div className="text-sm text-purple-700">Conversion Rate</div>
        </div>
        <div className="bg-yellow-50 p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-yellow-600">${(analytics.totalCreditsEarned / 100).toFixed(2)}</div>
          <div className="text-sm text-yellow-700">Credits Earned</div>
        </div>
      </div>
      
      {/* Referral Link */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Your Referral Link
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={referralLink}
            readOnly
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
          />
          <button
            id="copy-link-btn"
            onClick={copyReferralLink}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium"
          >
            Copy
          </button>
        </div>
      </div>

      {/* Add Referral */}
      <form onSubmit={createReferral} className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Refer a Friend ({referrals.filter(r => r.status === 'no_response').length}/3)
        </label>
        <div className="flex gap-2">
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="friend@example.com"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            disabled={creating || referrals.filter(r => r.status === 'no_response').length >= 3}
          />
          <button
            type="submit"
            disabled={creating || referrals.filter(r => r.status === 'no_response').length >= 3 || !newEmail.trim()}
            className="px-4 py-2 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white rounded-lg text-sm font-medium"
          >
            {creating ? 'Adding...' : 'Add'}
          </button>
        </div>
        {referrals.filter(r => r.status === 'no_response').length >= 3 && (
          <p className="text-sm text-gray-500 mt-1">
            Maximum 3 referrals. Cancel "No Response" referrals to add more.
          </p>
        )}
      </form>

      {/* Success/Error Messages */}
      {emailSuccess && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-600">✅ {emailSuccess}</p>
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Referrals List */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-700">Your Referrals</h4>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={hideCanceled}
              onChange={(e) => setHideCanceled(e.target.checked)}
              className="rounded border-gray-300"
            />
            Hide canceled
          </label>
        </div>
        {referrals.length === 0 ? (
          <p className="text-sm text-gray-500">No referrals yet. Start by adding a friend's email above!</p>
        ) : (
          <div className="space-y-3">
            {referrals
              .filter(referral => !hideCanceled || referral.status !== 'cancelled')
              .map((referral) => (
              <div key={referral.referral_id} className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{referral.referee_email}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Created {new Date(referral.created_at).toLocaleDateString()}
                      {referral.trial_started_at && (
                        <span className="ml-2">• Trial started {new Date(referral.trial_started_at).toLocaleDateString()}</span>
                      )}
                      {referral.completed_at && (
                        <span className="ml-2">• Completed {new Date(referral.completed_at).toLocaleDateString()}</span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(referral.status)}
                    {referral.status === 'no_response' && (
                      <>
                        <button
                          onClick={() => sendInvitationEmail(referral.referee_email)}
                          disabled={sendingEmails.has(referral.referee_email)}
                          className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded border border-blue-200 hover:bg-blue-50 disabled:opacity-50"
                        >
                          {sendingEmails.has(referral.referee_email) ? 'Sending...' : '📧 Send Email'}
                        </button>
                        <button
                          onClick={() => cancelReferral(referral.referral_id)}
                          className="text-xs text-red-600 hover:text-red-800 px-2 py-1 rounded border border-red-200 hover:bg-red-50"
                        >
                          Cancel
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {getProgressBar(referral.status)}
                {referral.status === 'complete' && referral.credit_amount && (
                  <div className="mt-2 text-xs text-green-600 font-medium">
                    🎉 Credit earned: ${(referral.credit_amount / 100).toFixed(2)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Credits Summary */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Referral Rewards</h4>
        <p className="text-sm text-blue-700">
          Earn 1 month free for each friend who completes their 14-day trial!
        </p>
        <p className="text-xs text-blue-600 mt-1">
          Completed referrals: {referrals.filter(r => r.status === 'complete').length}
        </p>
      </div>
    </div>
  );
}

export default ReferralManager;